/**
 * Tenants cuyo navbar lo manda el árbol del builder en TODAS las páginas.
 *
 * ## El comportamiento por defecto (todos los demás tenants)
 *
 * El navbar es fuente única: el dealer lo configura en la home, y el resto de
 * las páginas lo reciben del layout (`ConditionalNavbar` → `Navbar`), que lo
 * monta fijo arriba de todo. El nodo horneado en cada página no-home se borra
 * en `getPageBuilderData` (`stripNavbarNodes`) para que no salga duplicado ni
 * desincronizado. Eso NO cambia.
 *
 * ## Por qué existe la excepción
 *
 * La réplica de Eduardo Castro Automóviles (cliente 416) copia un header de dos
 * filas: arriba una franja oscura con logo, dirección y teléfono, y debajo la
 * barra de links. Ese orden se puede armar en la home —ahí el navbar se
 * renderiza inline— pero en las páginas internas el navbar del layout es un
 * overlay fijo en `top: 0`: queda SIEMPRE por encima de la franja, invirtiendo
 * las dos filas y mostrando el logo dos veces. No hay forma de resolverlo desde
 * el builder: la franja vive dentro del árbol de la página y el navbar del
 * layout vive fuera, antes en el DOM.
 *
 * Para los tenants de esta lista:
 *  - `getPageBuilderData` conserva el nodo navbar en las páginas no-home;
 *  - `ConditionalNavbar` no monta el navbar del layout;
 *  - `BuilderPageWrapper` no agrega el espaciador de 64px (acá el navbar ocupa
 *    flujo, no es overlay, así que no hay nada que compensar).
 *
 * El contrato que tiene que cumplir un tenant para entrar en la lista: que
 * TODAS sus páginas del builder traigan su propio nodo navbar. Si a una le
 * falta, esa página se queda sin menú — no hay red de contención, porque el
 * navbar del layout es justamente lo que se apaga.
 */
const TENANTS_CON_NAVBAR_INLINE = new Set<number>([
  416, // Eduardo Castro Automóviles — header de dos filas (franja oscura + menú)
]);

export function usaNavbarInline(clientId: unknown): boolean {
  const id = Number(clientId);
  return Number.isFinite(id) && TENANTS_CON_NAVBAR_INLINE.has(id);
}
